import React, { useState, useEffect } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getThemeClasses } from "../utils/themeClass";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import TopCarousel from "../carousel/carousel";
import ScrollableCategory from "../scrollablecategory/scrollableCategory";
import LazyLoadCategory from "../scrollablecategory/LazyLoadCategory";
import Loader from "../Loader/loader";
import LineSeparator from "../bgMadness/line";
import BgMadness from "../bgMadness/bgMadness";

import AllCategories from "../../data/category.json";
import AllGames from "../../data/gameData.json";
import { getFavorites } from "../utils/favorites";

// ReadMore Component
const ReadMore = ({ children }) => {
  const [expanded, setExpanded] = useState(false);
  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  return (
    <div>
      <div
        className={`transition-all duration-300 ease-in-out ${expanded ? "max-h-[2000px]" : "line-clamp-4"
          }`}
      >
        {children}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`mt-2 text-sm font-medium ${themeClass.accentText} ${themeClass.hoverAccentText} underline`}
      >
        {expanded ? "Read Less" : "Read More"}
      </button>
    </div>
  );
};

const Home = () => {
  const [gamesCarousels, setGamesCarousels] = useState([]);
  const [hotGames, setHotGames] = useState([]);
  const [mostPlayed, setMostPlayed] = useState([]);
  const [editorPicks, setEditorPicks] = useState([]);
  const [exclusiveGames, setExclusiveGames] = useState([]);
  const [bestGames, setBestGames] = useState([]);
  const [allGames, setAllGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favoriteGames, setFavoriteGames] = useState([]);

  const { theme } = useTheme();
  const themeClass = getThemeClasses(theme);

  const navigate = useNavigate();
  const selectedCategory = (title) =>
    navigate(`/categories?c=${encodeURIComponent(title)}`);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setGamesCarousels(AllGames.hotgames);
        setHotGames(AllGames.hotgames);
        setEditorPicks(AllGames.editorpicks);
        setExclusiveGames(AllGames.exclusivegames);
        setBestGames(AllGames.bestgames);
        setAllGames(AllGames.data);
        setMostPlayed(AllGames.mostPlayed);

        const storedFavs = getFavorites() || [];
        const matchedFavs = AllGames.data.filter((game) =>
          storedFavs.includes(game.id)
        );
        setFavoriteGames(matchedFavs);
      } catch (error) {
        console.error("Failed to fetch game data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <Loader />;

  return (
    <div className={`${themeClass.bgColor} ${themeClass.baseText} min-h-screen`}>
      <div
        className={`relative max-w-7xl mx-auto px-4 sm:px-6 py-12 overflow-hidden ${themeClass.bgColor} ${themeClass.baseText}`}
      >
        {/* Background Madness */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <BgMadness />
        </div>

        {/* Helmet SEO */}
        <Helmet>
          <title>Play Free Browser Games Online | BrowserArena</title>
          <meta
            name="description"
            content="Discover the best free browser games to play instantly. No downloads, just pure online gaming fun at BrowserArena!"
          />
          <link rel="canonical" href={`https://browserarena.com`} />
        </Helmet>

        {/* MAIN */}
        <div className="relative z-10 space-y-8">
          {/* Favorites */}
          {favoriteGames.length > 0 && (
            <ScrollableCategory
              category={{ title: "⭐ Favorites", id: -1 }}
              games={favoriteGames}
              className="border-glow"
            />
          )}

          <ScrollableCategory
            category={{ title: "⚡ Most Played", id: 0 }}
            games={mostPlayed}
            className="border-glow"
          />


          {/* Hot, Best, Editor Picks, Exclusive */}
          <ScrollableCategory
            category={{ title: "🔥 Hot Games", id: 0 }}
            games={hotGames}
            className="border-glow"
          />
          <ScrollableCategory
            category={{ title: "🏆 Best Games", id: 0 }}
            games={bestGames}
            className="border-glow"
          />

          {/* Intro Section */}
          <section
            className={`mt-12 px-6 py-8 max-w-5xl mx-auto rounded-2xl border ${themeClass.borderColor} ${themeClass.accentBg} backdrop-blur-md shadow-lg`}
          >
            <h2 className={`text-2xl sm:text-3xl font-bold mb-4 ${themeClass.accentText}`}>
              Play Free Online Browser Games Instantly
            </h2>
            <ReadMore>
              <p className="mb-3">
                Welcome to{" "}
                <span className={`${themeClass.accentText} font-semibold`}>
                  BrowserArena
                </span>{" "}
                – your go-to destination for the{" "}
                <span className="text-yellow-600 dark:text-yellow-400 font-semibold">
                  best free online games
                </span>.
              </p>
              <p className="mb-3">
                Play{" "}
                <span className={`${themeClass.accentText} font-semibold`}>
                  HTML5 games
                </span>{" "}
                instantly on mobile or desktop – no downloads or installs.
              </p>
              <p className="mb-3">
                Enjoy fast-loading games across puzzle, action, multiplayer,
                idle, and more – updated frequently.
              </p>
              <p>
                No login needed. Just tap, play, and enjoy{" "}
                <span className="text-yellow-500 dark:text-yellow-300 font-semibold">
                  endless browser fun
                </span>.
              </p>
            </ReadMore>
          </section>

          <ScrollableCategory
            category={{ title: "🎯 Editor Picks", id: 0 }}
            games={editorPicks}
            className="border-glow"
          />
          <ScrollableCategory
            category={{ title: "🚀 Exclusive Games", id: 0 }}
            games={exclusiveGames}
            className="border-glow"
          />

          {/* Dynamic Categories */}
          {AllCategories.map((category, index) => {
            const gamesArray = allGames.filter(
              (game) => game.category === category.title
            );
            return gamesArray.length ? (
              <div key={index} className="min-h-[100px] animate-fadeIn">
                <LazyLoadCategory>
                  <ScrollableCategory
                    category={category}
                    games={gamesArray}
                    className="border-glow"
                  />
                </LazyLoadCategory>
              </div>
            ) : null;
          })}

          {/* Category Grid */}
          <section
            className={`mt-20 px-6 py-10 max-w-5xl mx-auto rounded-2xl border ${themeClass.borderColor} ${themeClass.accentBg} backdrop-blur-lg shadow-md`}
          >
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {AllCategories.map((cat, index) => (
                <button
                  key={index}
                  onClick={() => selectedCategory(cat.title)}
                  aria-label={`View ${cat.title}`}
                  className={`text-sm font-semibold px-4 py-2 rounded-xl bg-slate-100 dark:bg-[#1a1a2e] ${themeClass.accentText} ${themeClass.borderColor} ${themeClass.hoverAccentText} ${themeClass.hoverBorder} transition duration-150 ease-in-out`}
                >
                  {cat.title}
                </button>
              ))}
            </div>
          </section>

          {/* Explore More */}
          <section
            className={`mt-20 px-6 py-10 max-w-5xl mx-auto rounded-2xl border ${themeClass.borderColor} ${themeClass.accentBg} backdrop-blur-lg shadow-md`}
          >
            <h3 className={`text-2xl sm:text-3xl font-bold mb-6 ${themeClass.accentText}`}>
              Explore More
            </h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-base">
              {[
                ["🔍 What Are Browser Games?", "/about-browser-games"],
                ["🏆 Best Browser Games 2025", "/best-browser-games"],
                ["🎮 Free Games – No Downloads", "/free-games"],
                ["⚙️ Top HTML5 Games", "/html5-games"],
                ["👥 Best Multiplayer Games", "/best-multiplayer"],
                ["📚 Educational Browser Games", "/educations"],
                ["💾 No Download Required", "/no-download"],
                ["⚡ Top Hypercasual Games", "/top-hypercasuals"],
                ["🌐 Why Play Online Games?", "/why-online"],
              ].map(([label, href], i) => (
                <li key={i}>
                  <a
                    href={href}
                    className={`${themeClass.hoverAccentText} hover:underline transition`}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </section>

          <LineSeparator />
        </div>
      </div>
    </div>
  );
};

export default Home;
